<script setup>
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import MessageApi from 'dashboard/api/inbox/message';
import { ORIENTATION } from './constants';

const props = defineProps({
  conversationId: { type: Number, required: true },
  messageId: { type: Number, required: true },
  reactions: { type: Object, default: () => ({}) },
  canReact: { type: Boolean, default: false },
  orientation: { type: String, default: ORIENTATION.LEFT },
});

const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const { t } = useI18n();
const isPickerOpen = ref(false);
const pickerRef = ref(null);
onClickOutside(pickerRef, () => {
  isPickerOpen.value = false;
});

const isRight = computed(() => props.orientation === ORIENTATION.RIGHT);
const reactionList = computed(() => Object.values(props.reactions || {}));

const react = async emoji => {
  isPickerOpen.value = false;
  const emojiToSend = props.reactions?.business === emoji ? '' : emoji;
  try {
    await MessageApi.react(props.conversationId, props.messageId, emojiToSend);
  } catch (error) {
    useAlert(t('CONVERSATION.REACTIONS.ERROR'));
  }
};
</script>

<template>
  <div
    v-if="reactionList.length"
    class="absolute -bottom-3 flex gap-0.5 px-1.5 py-0.5 text-sm rounded-full bg-n-solid-3 outline outline-1 outline-n-container"
    :class="isRight ? 'ltr:right-3 rtl:left-3' : 'ltr:left-3 rtl:right-3'"
  >
    <span v-for="emoji in reactionList" :key="emoji">{{ emoji }}</span>
  </div>
  <div
    v-if="canReact"
    ref="pickerRef"
    class="absolute top-1/2 -translate-y-1/2"
    :class="
      isRight
        ? 'ltr:right-full rtl:left-full mr-1'
        : 'ltr:left-full rtl:right-full ml-1'
    "
  >
    <button
      type="button"
      class="grid size-6 rounded-full place-content-center bg-n-solid-3 text-n-slate-11 outline outline-1 outline-n-container opacity-0 group-hover/reaction:opacity-100 focus:opacity-100 transition-opacity"
      :class="{ '!opacity-100': isPickerOpen }"
      :title="t('CONVERSATION.REACTIONS.REACT')"
      @click.stop="isPickerOpen = !isPickerOpen"
    >
      <span class="i-lucide-smile-plus size-4" />
    </button>
    <div
      v-if="isPickerOpen"
      class="absolute z-50 flex gap-1 p-1.5 rounded-full shadow-xl bottom-full mb-1 bg-n-alpha-3 backdrop-blur-[100px] outline outline-1 outline-n-container"
      :class="isRight ? 'ltr:right-0 rtl:left-0' : 'ltr:left-0 rtl:right-0'"
    >
      <button
        v-for="emoji in REACTION_EMOJIS"
        :key="emoji"
        type="button"
        class="grid text-xl rounded-full size-8 place-content-center hover:bg-n-alpha-2 hover:scale-110 transition-transform"
        :class="{ 'bg-n-solid-blue': reactions?.business === emoji }"
        @click.stop="react(emoji)"
      >
        {{ emoji }}
      </button>
    </div>
  </div>
</template>
